import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const ActionBadge = ({ action }) => {
  const map = {
    approved: { cls: 'bg-success/10 text-success', icon: 'CheckCircle' },
    rejected: { cls: 'bg-error/10 text-error', icon: 'XCircle' },
    flagged: { cls: 'bg-orange-500/10 text-orange-400', icon: 'Flag' },
    escalated: { cls: 'bg-warning/10 text-warning', icon: 'AlertTriangle' },
    reviewed: { cls: 'bg-primary/10 text-primary', icon: 'Eye' },
  };
  const style = map?.[action] || { cls: 'bg-muted text-muted-foreground', icon: 'Activity' };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium ${style?.cls}`}>
      <Icon name={style?.icon} size={11} />
      {action}
    </span>
  );
};

const AuditLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [actionFilter, setActionFilter] = useState('all');
  const [securityAlerts, setSecurityAlerts] = useState([]);

  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase?.from('moderation_logs')
        ?.select(`id, action, reason, notes, created_at,
          moderator:user_profiles!moderation_logs_moderator_id_fkey(full_name, email),
          target:user_profiles!moderation_logs_target_user_id_fkey(full_name, email),
          transaction:transactions!moderation_logs_transaction_id_fkey(id, amount),
          campaign:campaigns!moderation_logs_campaign_id_fkey(title)`)
        ?.order('created_at', { ascending: false })
        ?.limit(200);

      if (!error && data) {
        const mapped = data?.map(l => ({
          id: l?.id,
          action: l?.action,
          moderator: l?.moderator?.full_name || l?.moderator?.email || 'System',
          target: l?.target?.full_name || l?.target?.email || (l?.transaction?.id ? 'Transaction' : l?.campaign?.title || '—'),
          reason: l?.reason || l?.notes || '',
          amount: l?.transaction?.amount ? `$${parseFloat(l?.transaction?.amount)?.toFixed(2)}` : null,
          campaign: l?.campaign?.title || null,
          date: new Date(l?.created_at)?.toLocaleString(),
          timestamp: new Date(l?.created_at),
          refId: l?.id?.split('-')?.[0]?.toUpperCase(),
        }));
        setLogs(mapped);

        // Detect security alerts: multiple rejections in short time, unusual patterns
        const recentRejections = mapped?.filter(l => l?.action === 'rejected' && (Date.now() - l?.timestamp?.getTime()) < 3600000);
        const alerts = [];
        if (recentRejections?.length > 5) {
          alerts?.push({ type: 'warning', message: `${recentRejections?.length} rejections in the last hour — possible suspicious activity` });
        }
        const suspendedCount = mapped?.filter(l => l?.notes?.includes('suspended') && (Date.now() - l?.timestamp?.getTime()) < 86400000)?.length;
        if (suspendedCount > 3) {
          alerts?.push({ type: 'error', message: `${suspendedCount} account suspensions in the last 24 hours` });
        }
        setSecurityAlerts(alerts);
      }
    } catch (err) {
      console.error('[AuditLogs] loadLogs error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadLogs(); }, [loadLogs]);

  const filtered = logs?.filter(l => {
    const matchSearch = l?.moderator?.toLowerCase()?.includes(search?.toLowerCase()) || l?.target?.toLowerCase()?.includes(search?.toLowerCase()) || l?.refId?.toLowerCase()?.includes(search?.toLowerCase());
    const matchAction = actionFilter === 'all' || l?.action === actionFilter;
    return matchSearch && matchAction;
  });

  const handleExport = () => {
    const headers = ['Ref ID', 'Action', 'Moderator', 'Target', 'Reason', 'Date'];
    const rows = filtered?.map(l => [l?.refId, l?.action, l?.moderator, l?.target, l?.reason, l?.date]);
    const csv = [headers, ...rows]?.map(r => r?.map(c => `"${String(c)?.replace(/"/g, '""')}"`)?.join(','))?.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `audit_logs_${new Date()?.toISOString()?.split('T')?.[0]}.csv`;
    document.body?.appendChild(a); a?.click(); document.body?.removeChild(a); URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-5">
      {/* Security alerts */}
      {securityAlerts?.length > 0 && (
        <div className="space-y-2">
          {securityAlerts?.map((alert, i) => (
            <div key={i} className={`flex items-center gap-3 p-4 rounded-xl border ${
              alert?.type === 'error' ? 'bg-error/10 border-error/30' : 'bg-warning/10 border-warning/30'
            }`}>
              <Icon name="AlertTriangle" size={18} color={alert?.type === 'error' ? 'var(--color-error)' : 'var(--color-warning)'} />
              <p className={`text-sm font-medium ${alert?.type === 'error' ? 'text-error' : 'text-warning'}`}>{alert?.message}</p>
            </div>
          ))}
        </div>
      )}
      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: 'Total Actions', value: logs?.length, icon: 'Activity', color: 'text-primary', bg: 'bg-primary/10' },
          { label: 'Approvals', value: logs?.filter(l => l?.action === 'approved')?.length, icon: 'CheckCircle', color: 'text-success', bg: 'bg-success/10' },
          { label: 'Rejections', value: logs?.filter(l => l?.action === 'rejected')?.length, icon: 'XCircle', color: 'text-error', bg: 'bg-error/10' },
          { label: 'Flagged', value: logs?.filter(l => l?.action === 'flagged')?.length, icon: 'Flag', color: 'text-warning', bg: 'bg-warning/10' },
        ]?.map((s, i) => (
          <div key={i} className="bg-card rounded-xl border border-border p-4 flex items-center gap-3">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${s?.bg}`}>
              <Icon name={s?.icon} size={16} className={s?.color} />
            </div>
            <div>
              <p className="text-lg font-bold text-foreground">{s?.value}</p>
              <p className="text-xs text-muted-foreground">{s?.label}</p>
            </div>
          </div>
        ))}
      </div>
      <div className="bg-card rounded-xl border border-border p-5">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-5">
          <div>
            <h3 className="text-base font-semibold text-foreground">Audit Log</h3>
            <p className="text-sm text-muted-foreground">{filtered?.length} entries</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="w-48"><Input type="search" placeholder="Search logs..." value={search} onChange={(e) => setSearch(e?.target?.value)} /></div>
            <div className="w-36">
              <Select
                options={[{ value: 'all', label: 'All Actions' }, { value: 'approved', label: 'Approved' }, { value: 'rejected', label: 'Rejected' }, { value: 'flagged', label: 'Flagged' }, { value: 'reviewed', label: 'Reviewed' }, { value: 'escalated', label: 'Escalated' }]}
                value={actionFilter} onChange={setActionFilter}
              />
            </div>
            <Button variant="outline" size="sm" iconName="Download" iconPosition="left" onClick={handleExport}>Export</Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-7 h-7 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  {['Ref ID', 'Action', 'Moderator', 'Target / Subject', 'Reason / Notes', 'Amount', 'Date']?.map(h => (
                    <th key={h} className="text-left py-3 px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered?.map((l) => (
                  <tr key={l?.id} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                    <td className="py-3 px-3">
                      <span className="text-xs font-mono text-muted-foreground bg-muted px-2 py-0.5 rounded">{l?.refId}</span>
                    </td>
                    <td className="py-3 px-3"><ActionBadge action={l?.action} /></td>
                    <td className="py-3 px-3"><span className="text-sm text-foreground">{l?.moderator}</span></td>
                    <td className="py-3 px-3"><span className="text-sm text-foreground max-w-[140px] truncate block">{l?.target}</span></td>
                    <td className="py-3 px-3"><span className="text-xs text-muted-foreground max-w-[160px] truncate block">{l?.reason || '—'}</span></td>
                    <td className="py-3 px-3">{l?.amount ? <span className="text-sm font-medium text-success">{l?.amount}</span> : <span className="text-muted-foreground">—</span>}</td>
                    <td className="py-3 px-3"><span className="text-xs text-muted-foreground">{l?.date}</span></td>
                  </tr>
                ))}
                {filtered?.length === 0 && (
                  <tr><td colSpan={7} className="py-10 text-center text-sm text-muted-foreground">No audit logs found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuditLogs;
